import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, asset } from "@/lib/api";
import { Link } from "react-router-dom";
import { Award, Bookmark, BookOpen, Calendar, Download, LogOut, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/me/enrollments").then(r => r.data).catch(() => []),
      api.get("/me/progress").then(r => r.data).catch(() => []),
      api.get("/me/rsvps").then(r => r.data).catch(() => []),
      api.get("/courses").then(r => r.data),
    ]).then(([e, p, r, c]) => { setEnrollments(e); setProgress(p); setRsvps(r); setCourses(c); });
  }, []);

  const savedCourses = courses.filter(c => enrollments.some(e => e.course_id === c.id));
  const suggested = courses.filter(c => !enrollments.some(e => e.course_id === c.id)).slice(0, 3);

  const percent = (courseId) => {
    const c = courses.find(cc => cc.id === courseId);
    if (!c) return 0;
    const done = progress.filter(p => p.course_id === courseId && p.completed).length;
    return c.lessons_count ? Math.round((done / c.lessons_count) * 100) : 0;
  };

  return (
    <div>
      <section className="border-b border-border">
        <div className="container-x py-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Member Dashboard</div>
            <h1 className="font-display text-5xl md:text-6xl leading-[1] mt-2">Welcome back, <span className="italic text-primary">{user?.name?.split(" ")[0]}</span>.</h1>
            <p className="mt-4 text-muted-foreground max-w-xl">Track your certifications, enrollments and community activity in one place.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/learn" className="btn-primary" data-testid="dash-cta-learn">Continue learning</Link>
            <button onClick={logout} data-testid="dash-logout" className="btn-outline"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </section>

      <section className="container-x py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-14">
          <div>
            <div className="eyebrow">Your courses</div>
            {savedCourses.length === 0 ? (
              <div className="mt-4 rounded-lg border border-border p-8 text-center text-muted-foreground">
                You haven't enrolled in any courses yet. <Link to="/learn" className="text-foreground font-semibold">Browse the catalog →</Link>
              </div>
            ) : (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {savedCourses.map(c => (
                  <Link to={`/learn/${c.id}`} key={c.id} data-testid={`dash-course-${c.id}`} className="group rounded-lg border border-border overflow-hidden bg-card">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img src={asset(c.cover)} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <div className="font-display text-xl">{c.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{c.track}</div>
                      <Progress value={percent(c.id)} className="mt-4" />
                      <div className="mt-2 text-xs text-muted-foreground">{percent(c.id)}% complete</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="eyebrow">Suggested for you</div>
            <div className="mt-4 grid md:grid-cols-3 gap-4">
              {suggested.map(c => (
                <Link to={`/learn/${c.id}`} key={c.id} className="rounded-lg border border-border overflow-hidden bg-card group">
                  <div className="relative aspect-[16/10]">
                    <img src={asset(c.cover)} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg leading-tight">{c.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{c.track}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 eyebrow"><Award className="h-3.5 w-3.5" /> Certifications</div>
            <div className="mt-4 space-y-3">
              {["Future Owner","Ownership 101","Ownership 201"].map((c, i) => (
                <div key={c} className="flex items-center justify-between border-b border-border pb-3 last:border-0">
                  <div>
                    <div className="font-semibold">{c}</div>
                    <div className="text-xs text-muted-foreground">{i === 2 ? "In progress" : "Completed"}</div>
                  </div>
                  <button className="btn-outline !py-1.5 !px-3 text-[10px]"><Download className="h-3 w-3" /> PDF</button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 eyebrow"><Calendar className="h-3.5 w-3.5" /> Upcoming RSVPs</div>
            <div className="mt-4 text-sm text-muted-foreground">
              {rsvps.length === 0 ? "No upcoming RSVPs — head to Events." : `${rsvps.length} events on your calendar.`}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 eyebrow"><Bookmark className="h-3.5 w-3.5" /> Bookmarks</div>
            <div className="mt-4 text-sm text-muted-foreground">Save resources from the library to see them here.</div>
          </div>

          <div className="rounded-lg border border-border bg-primary text-primary-foreground p-6">
            <div className="flex items-center gap-2 eyebrow text-primary-foreground/90"><Sparkles className="h-3.5 w-3.5" /> Membership</div>
            <div className="font-display text-3xl mt-2">Village Member</div>
            <div className="text-sm mt-2 opacity-90">Full access to courses, certifications and community events. Premium plans coming soon.</div>
          </div>
        </aside>
      </section>
    </div>
  );
}
